'use client';

import {
  CaseStudyBody,
  CaseStudyResultsTable,
} from '@/components/case-study/case-study-body';
import { CaseStudyHero } from '@/components/case-study/case-study-hero';
import { CustomerVideos } from '@/components/sections/customer-videos';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

/* SYARAH — cleared for the film, not for figures.
 *
 * NO AUTOMATION RATE. The marketing tracker carries Syarah as "Hold - do
 * not send", and the only rate-shaped number on record is a day-1 4.7% that
 * was logged as a fault under investigation, on <=50% of conversations,
 * office hours only. It is not a result and must never reach the page. The
 * results table below therefore carries no percentage of ours — it reports
 * qualitative outcomes, the same deliberate deviation Snoonu makes.
 *
 * THE BODY COMES FROM THE FILM, NOT THE TEMPLATE. Every paragraph below is
 * sourced to what Syarah's own people say on camera: the first AI project,
 * the dialect worry, the doubling-headcount alternative, the vendor bake-off
 * decided on Arabic, the gradual rollout, response speed as the managed
 * metric. Nothing is carried over from another story. If you extend it, work
 * from the transcript — the film states no automation rate, and inventing one
 * to fill the results table is the exact failure this page exists to avoid.
 *
 * THE ONE FIGURE IS THEIRS, NOT OURS. ~95% is Syarah describing its own
 * customers' language preference, stated on camera in a co-branded film. It
 * is not an Open performance claim and must not be relabelled as one.
 *
 * VIDEO IS /ar ONLY. The interview is spoken in Arabic, so it only earns its
 * place on the Arabic site — same gate as OTO and Qoyod. English falls back
 * to the text-and-stats hero.
 */

const SyarahCaseStudy = () => {
  const { t, isRTL } = useTranslation();
  return (
    <>
      <CaseStudyHero
        customerName="Syarah"
        title={t.customerStories['syarah_hero_title']}
        subtitle={t.customerStories['syarah_what_we_did']}
        // None of these is an Open performance claim: two are facts about
        // the company, and the 95% is Syarah's own statement about its
        // customers. Do not add an automation rate here.
        stats={[
          {
            // Syarah's own on-camera figure about its customers' language,
            // not a performance number. See the note above.
            value: isRTL ? '٩٥٪' : '95%',
            label: t.customerStories['Customers who prefer Arabic'],
          },
          {
            value: isRTL ? 'السعودية' : 'Saudi Arabia',
            label: t.customerStories['Live market'],
          },
          {
            value: isRTL ? 'سيارات مستعملة' : 'Used cars',
            label: t.customerStories['Category'],
          },
        ]}
        videoSrc={isRTL ? '/home/stories/syarah.mp4' : undefined}
        /* No videoStartAt. OTO and Qoyod seek past dead air at the top of
         * their films; this one opens cold on the Open | سيارة lockup, held
         * from frame 0 to ~3.5s, and that intro should play. The resting frame
         * is not this one — it is the registered still (lib/video-posters),
         * which the card holds until the first press. */
      />

      <CaseStudyBody
        aside={{
          logoSrc: '/logos/logo-syarah.svg',
          logoAlt: 'Syarah',
          customerName: 'Syarah',
          company: t.customerStories['syarah_company_description'],
          industry: t.customerStories['Automotive / Marketplace'],
          websiteUrl: 'https://syarah.com',
          // Empty until a cleared figure exists — the aside omits the block
          // entirely rather than showing an empty "Key Results" heading.
          keyResults: [],
          extras: [
            {
              label: t.customerStories.Location,
              value: t.customerStories['Riyadh, Saudi Arabia'],
            },
          ],
        }}
      >
        <p>{t.customerStories['syarah_intro']}</p>

        <h2>{t.customerStories['syarah_challenge_title']}</h2>
        <p>{t.customerStories['syarah_challenge_p1']}</p>
        <p>{t.customerStories['syarah_challenge_p2']}</p>
        <p>{t.customerStories['syarah_challenge_p3']}</p>

        <h2>{t.customerStories['syarah_solution_title']}</h2>
        <p>{t.customerStories['syarah_solution_p1']}</p>
        <p>{t.customerStories['syarah_solution_p2']}</p>
        <p>{t.customerStories['syarah_solution_p3']}</p>
        <p>{t.customerStories['syarah_solution_p4']}</p>

        <h3>{t.customerStories['The Results']}</h3>
        <p>{t.customerStories['syarah_results_intro']}</p>

        {/* Every impact here is qualitative on purpose. The film states no
         * automation rate, and none is cleared, so the table reports what the
         * team actually said rather than a number standing in for it. */}
        <CaseStudyResultsTable
          metricLabel={t.customerStories['Metric']}
          impactLabel={t.customerStories['Impact']}
          rows={[
            {
              metric: t.customerStories['Response speed'],
              impact:
                t.customerStories['Faster than hiring would have reached'],
            },
            {
              metric: t.customerStories['Customer language'],
              impact: t.customerStories['Arabic — about 95% of customers'],
            },
            {
              metric: t.customerStories['Rollout'],
              impact:
                t.customerStories['Gradual — widened each time quality held'],
            },
            {
              metric: t.customerStories['Escalation path'],
              impact: t.customerStories['Cases that need human judgment'],
            },
          ]}
        />

        <p>{t.customerStories['syarah_results_p1']}</p>

        <h2>{t.customerStories['syarah_impact_title']}</h2>
        <p>{t.customerStories['syarah_impact_p1']}</p>
        <p>{t.customerStories['syarah_impact_p2']}</p>
      </CaseStudyBody>

      <CustomerVideos />

      <Testimonials />
    </>
  );
};

export default SyarahCaseStudy;
